import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DEFAULT_ROOM_TIMEZONE,
  FISCAL_YEAR_END_MONTHS,
  fiscalYearEndLabel,
  timeZoneOptions,
} from '@dataroom/shared';
import type { Room } from '@dataroom/shared';
import { useDdPhases, useSetRoomFiscalCalendar } from '@/api/hooks';
import { plainError } from '@/lib/errors';

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The room's fiscal year end + timezone (migration 0057, ADR-0040). The
 * checklist's period requests are generated from them once, when the checklist is
 * created, so they can be changed only until then: with a checklist the fields
 * are read-only and say why (the server refuses a change with a 409 too).
 */
export function FiscalCalendarCard({ room }: { room: Room }) {
  const phases = useDdPhases(room.id);
  const save = useSetRoomFiscalCalendar(room.id);
  const savedMonth = room.fiscalYearEndMonth ?? 12;
  const savedZone = room.timezone ?? DEFAULT_ROOM_TIMEZONE;
  const [month, setMonth] = useState(savedMonth);
  const [timezone, setTimezone] = useState(savedZone);
  useEffect(() => {
    setMonth(savedMonth);
    setTimezone(savedZone);
  }, [savedMonth, savedZone]);

  const locked = (phases.data?.length ?? 0) > 0;
  const dirty = month !== savedMonth || timezone !== savedZone;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fiscal year</CardTitle>
        <p className="text-sm text-muted-foreground">
          The checklist&apos;s yearly, quarterly and year-to-date requests follow the company&apos;s
          fiscal year: with June 30, &ldquo;FY2025&rdquo; is July 2024 – June 2025 and
          &ldquo;FY2025 Q1&rdquo; is July – September 2024.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="room-fye" className="text-sm font-medium">
              Fiscal year end
            </label>
            <select
              id="room-fye"
              className={SELECT_CLASS}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              disabled={locked || save.isPending}
            >
              {FISCAL_YEAR_END_MONTHS.map((m) => (
                <option key={m} value={m}>
                  {fiscalYearEndLabel(m)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="room-tz" className="text-sm font-medium">
              Timezone
            </label>
            <select
              id="room-tz"
              className={SELECT_CLASS}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={locked || save.isPending}
            >
              {timeZoneOptions(timezone).map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
        {locked ? (
          <p className="text-xs text-muted-foreground">
            Fixed: this room&apos;s checklist was created with this fiscal year, so its requests
            already name their periods.
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => save.mutate({ fiscalYearEndMonth: month, timezone })}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save fiscal year'}
            </Button>
            {save.error ? (
              <p className="text-sm text-destructive">{plainError(save.error)}</p>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
